import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

function QRScanner({ onScan, onClose, isScanning, successMessage, error: externalError }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    startScanner();
    
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScanner = async () => {
    try {
      setError('');
      
      // First, check if we can get camera permissions
      console.log('[QR] Requesting camera permissions...');
      
      // Test camera access first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        console.log('[QR] Camera permission granted');
        // Stop the test stream
        stream.getTracks().forEach(track => track.stop());
      } catch (permError) {
        console.error('[QR] Camera permission error:', permError);
        if (permError.name === 'NotAllowedError') {
          throw new Error('Camera permission denied. Please allow camera access in your browser settings.');
        } else if (permError.name === 'NotFoundError') {
          throw new Error('No camera found on this device.');
        } else if (permError.name === 'NotSupportedError') {
          throw new Error('Camera access requires HTTPS. Please use https:// or localhost.');
        } else {
          throw new Error(`Camera error: ${permError.message}`);
        }
      }

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      await html5QrCode.start(
        { facingMode: "environment" }, // Use back camera
        config,
        (decodedText) => {
          // QR Code detected!
          console.log('[QR] Code detected:', decodedText);
          setScanning(true);
          onScan(decodedText);
          stopScanner();
        },
        (errorMessage) => {
          // Ignore continuous scan errors
        }
      );
      
      setCameraReady(true);
      console.log('[QR] Scanner ready');
    } catch (err) {
      console.error('[QR] Scanner error:', err);
      setError(err.message || 'Camera access denied or not available. Please enable camera permissions or use manual input below.');
      setCameraReady(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4">
      <div className="scanline"></div>
      <div className="bg-dark-lighter rounded-lg shadow-neon-blue border border-cyber-blue max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-cyber-blue font-mono">[SCAN] QR_DECODER</h3>
          <button
            onClick={onClose}
            className="text-cyber-red hover:text-cyber-red/70 text-3xl transition"
          >
            ×
          </button>
        </div>

        {/* Scanning Status */}
        {(isScanning || scanning) && (
          <div className="bg-primary/20 border border-primary rounded-lg p-4 mb-4 text-center">
            <div className="text-4xl mb-2 animate-spin">🔄</div>
            <p className="text-primary font-mono text-sm animate-pulse">
              [PROCESSING] Decoding signature...
            </p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-primary/20 border border-primary rounded-lg p-4 mb-4 text-center animate-pulse">
            <div className="text-4xl mb-2">✓</div>
            <p className="text-primary font-mono text-sm font-bold">
              {successMessage}
            </p>
          </div>
        )}

        {/* Camera View */}
        <div className="relative mb-4">
          <div 
            id="qr-reader" 
            ref={scannerRef}
            className="rounded-lg overflow-hidden border-2 border-primary/50"
            style={{ width: '100%' }}
          />
          
          {!cameraReady && !error && (
            <div className="absolute inset-0 bg-dark rounded-lg flex items-center justify-center border border-primary/30">
              <div className="text-center">
                <div className="text-6xl mb-4 animate-pulse">📷</div>
                <p className="text-matrix-green font-mono text-sm">
                  [INIT] Starting camera feed...
                </p>
              </div>
            </div>
          )}

          {cameraReady && (
            <div className="mt-2 text-center">
              <p className="text-cyber-blue font-mono text-xs">
                &gt;&gt; CAMERA_ACTIVE | Position QR code in frame
              </p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {(error || externalError) && (
          <div className="bg-cyber-red/10 border border-cyber-red rounded-lg p-4 mb-4">
            <p className="text-cyber-red font-mono text-sm mb-3">
              [ERROR] {externalError || error}
            </p>
            {error && (
              <>
                <button
                  onClick={startScanner}
                  className="bg-cyber-red text-white px-4 py-2 rounded-lg hover:bg-cyber-red/80
                           transition-colors font-semibold font-mono text-sm border-2 border-cyber-red
                           hover:shadow-neon-red mb-2 w-full"
                >
                  [RETRY] Request Camera Access
                </button>
                <div className="text-xs text-cyber-red/70 font-mono mt-2 space-y-1">
                  <p>➤ On mobile: Tap "Allow" when browser asks for camera</p>
                  <p>➤ If no popup: Check browser settings → Site permissions → Camera</p>
                  <p>➤ Chrome mobile: Settings → Site settings → Camera → Allow</p>
                  <p>➤ Safari iOS: Settings → Safari → Camera → Allow</p>
                  <p>➤ Note: Camera requires HTTPS (except on localhost)</p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default QRScanner;
